/**
 * Before you edit these, read the documentation on how these files are compiled:
 * https://docs.phpvms.net/developers/building-assets
 *
 * Edits here don't take place until you compile these assets and then upload them.
 */

import draw_base_map from './base_map';

import { ACTUAL_ROUTE_COLOR } from './config';

import request from '../request';
import {LatLng} from "leaflet/dist/leaflet-src.esm";

// const geolib = require('geolib');
const leaflet = require('leaflet');
const rivets = require('rivets');

/**
 * Render the live map
 * @param _opts
 * @private
 */
export default (_opts) => {
  const opts = Object.assign({
    center: [29.98139, -95.33374],
    refresh_interval: 10, // seconds
    zoom: 5,
    acars_uri: '/api/acars',
    update_uri: '/api/acars/geojson',
    pirep_uri: '/api/pireps/{id}',
    pirep_link_uri: '/pireps/{id}',
    positions: null,
    render_elem: 'map',
    aircraft_icon: '/assets/img/acars/aircraft.png',
    flown_route_color: ACTUAL_ROUTE_COLOR,
    units: 'nmi',
  }, _opts);

  const map = draw_base_map(opts);

  /*
   * SKY BLUE WEATHER LAYERS
   */

  let standardBaseLayer = null;

  map.eachLayer((layer) => {
    if (standardBaseLayer === null && layer instanceof leaflet.TileLayer) {
      standardBaseLayer = layer;
    }
  });

  const darkOperationsLayer = leaflet.tileLayer.provider(
    'CartoDB.DarkMatter',
    {
      maxZoom: 19,
    }
  );

  const noaaRadarLayer = leaflet.tileLayer.wms(
    'https://mapservices.weather.noaa.gov/eventdriven/services/radar/radar_base_reflectivity/MapServer/WMSServer',
    {
      layers: '0',
      format: 'image/png',
      transparent: true,
      opacity: 0.58,
      version: '1.3.0',
      attribution: 'Radar: NOAA/NWS',
      zIndex: 300,
    }
  );

  const baseMaps = {};

  if (standardBaseLayer !== null) {
    baseMaps['Standard Map'] = standardBaseLayer;
  }

  baseMaps['Dark Operations'] = darkOperationsLayer;

  const windsAloftLayer = leaflet.layerGroup();
  let selectedWindAltitude = 12000;

  leaflet.control.layers(
    baseMaps,
    {
      'NOAA Weather Radar': noaaRadarLayer,
      'Winds Aloft': windsAloftLayer,
    },
    {
      position: 'topright',
      collapsed: true,
    }
  ).addTo(map);


  /*
   * SKY BLUE WINDS ALOFT DISPLAY
   */

  const WindsAltitudeControl = leaflet.Control.extend({
    options: {
      position: 'topright',
    },

    onAdd() {
      const container = leaflet.DomUtil.create(
        'div',
        'sbra-winds-control leaflet-control'
      );

      const label = leaflet.DomUtil.create(
        'label',
        'sbra-winds-label',
        container
      );

      label.innerHTML = 'Winds Aloft';

      const select = leaflet.DomUtil.create(
        'select',
        'sbra-winds-select',
        container
      );

      [
        3000,
        6000,
        9000,
        12000,
        18000,
        24000,
        30000,
        34000,
        39000,
      ].forEach((altitude) => {
        const option = document.createElement('option');

        option.value = altitude;
        option.textContent = altitude.toLocaleString() + ' ft';

        if (altitude === selectedWindAltitude) {
          option.selected = true;
        }

        select.appendChild(option);
      });

      leaflet.DomEvent.disableClickPropagation(container);
      leaflet.DomEvent.disableScrollPropagation(container);

      select.addEventListener('change', () => {
        selectedWindAltitude = parseInt(select.value, 10);
        if (map.hasLayer(windsAloftLayer)) {
          loadWindsAloft(selectedWindAltitude);
        }
      });


      return container;
    },
  });

  // Standalone winds selector replaced by Aviation Weather toolbar.


  function windArrowHtml(direction, speed) {
    if (speed === 0) {
      return '<div class="sbra-wind-calm">C</div>';
    }

    return (
      '<div class="sbra-wind-marker">' +
        '<div class="sbra-wind-arrow" style="transform: rotate(' +
          direction +
          'deg)">&#8593;</div>' +
        '<div class="sbra-wind-speed">' + speed + '</div>' +
      '</div>'
    );
  }

  function loadWindsAloft(altitude) {
    fetch('/api/weather/winds-aloft?altitude=' + altitude)
      .then((response) => {
        if (!response.ok) {
          throw new Error('Winds request failed');
        }

        return response.json();
      })
      .then((data) => {
        windsAloftLayer.clearLayers();

        data.stations.forEach((station) => {
          const wind = station.winds[String(altitude)];

          if (!wind) {
            return;
          }

          const icon = leaflet.divIcon({
            className: 'sbra-wind-icon',
            html: windArrowHtml(
              wind.direction_deg,
              wind.speed_kt
            ),
            iconSize: [44, 44],
            iconAnchor: [22, 22],
          });

          const marker = leaflet.marker(
            [station.lat, station.lon],
            {
              icon: icon,
              keyboard: false,
            }
          );

          const temperature = wind.temperature_c === null
            ? 'Not reported'
            : wind.temperature_c + '&deg;C';

          marker.bindPopup(
            '<div class="sbra-wind-popup">' +
              '<strong>' + station.icao + '</strong><br>' +
              station.name + '<br><br>' +
              '<strong>Altitude:</strong> ' +
                altitude.toLocaleString() + ' ft<br>' +
              '<strong>Wind:</strong> ' +
                wind.direction_deg + '&deg; at ' +
                wind.speed_kt + ' kt<br>' +
              '<strong>Temperature:</strong> ' +
                temperature + '<br>' +
              '<strong>Forecast:</strong> ' +
                data.valid_time +
            '</div>'
          );

          marker.addTo(windsAloftLayer);
        });
      })
      .catch((error) => {
        console.warn('Winds aloft could not be loaded', error);
      });
  }

  map.on('overlayadd', (event) => {
    if (event.layer === windsAloftLayer) {
      loadWindsAloft(selectedWindAltitude);
    }
  });

  /* END SKY BLUE WINDS ALOFT DISPLAY */





  /*
   * SKY BLUE G-AIRMET FOUNDATION
   */

  const gairmetLayer = leaflet.layerGroup();

  function gairmetStyle(feature) {
    const hazard = String(
      feature.properties.hazard || "UNKNOWN"
    ).toUpperCase();

    const styles = {
      IFR: {
        color: "#7c3aed",
        fillColor: "#a78bfa",
      },
      MT_OBSC: {
        color: "#475569",
        fillColor: "#94a3b8",
      },
      TURB_HI: {
        color: "#d97706",
        fillColor: "#f59e0b",
      },
      TURB_LO: {
        color: "#ea580c",
        fillColor: "#fb923c",
      },
      ICE: {
        color: "#2563eb",
        fillColor: "#60a5fa",
      },
      FZLVL: {
        color: "#0284c7",
        fillColor: "#38bdf8",
      },
      SFC_WND: {
        color: "#0f766e",
        fillColor: "#2dd4bf",
      },
      LLWS: {
        color: "#be123c",
        fillColor: "#fb7185",
      },
      UNKNOWN: {
        color: "#334155",
        fillColor: "#64748b",
      },
    };

    return {
      ...(styles[hazard] || styles.UNKNOWN),
      weight: 2,
      opacity: 0.9,
      fillOpacity: 0.18,
      dashArray: "6 4",
    };
  }

  function gairmetValue(value, fallback = "Not reported") {
    if (value === null || value === undefined || value === "") {
      return fallback;
    }

    return value;
  }

  function formatGAirmetTime(value) {
    if (!value) {
      return "Time unavailable";
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return value;
    }

    return date.toLocaleString([], {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      timeZoneName: "short",
    });
  }

  function loadGAirmets() {
    return fetch('/api/weather/gairmets', {
      headers: {
        Accept: 'application/json',
      },
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error('G-AIRMET request failed');
        }

        return response.json();
      })
      .then((data) => {
        gairmetLayer.clearLayers();

        console.log(
          'Sky Blue G-AIRMET features loaded:',
          (data.features || []).length
        );

        return data.features || [];
      })
      .catch((error) => {
        console.error('Unable to load G-AIRMET data:', error);
        return [];
      });
  }

  /* END SKY BLUE G-AIRMET FOUNDATION */

  /*
   * SKY BLUE SIGMET FOUNDATION
   */

  const sigmetLayer = leaflet.layerGroup();

  function sigmetStyle(feature) {
    const hazard = String(
      feature.properties.hazard || "UNKNOWN"
    ).toUpperCase();

    const styles = {
      CONVECTIVE: {
        color: "#c62828",
        fillColor: "#ef4444",
      },
      TURB: {
        color: "#d97706",
        fillColor: "#f59e0b",
      },
      ICE: {
        color: "#2563eb",
        fillColor: "#60a5fa",
      },
      IFR: {
        color: "#7c3aed",
        fillColor: "#a78bfa",
      },
      MTN_OBSCN: {
        color: "#475569",
        fillColor: "#94a3b8",
      },
      UNKNOWN: {
        color: "#334155",
        fillColor: "#64748b",
      },
    };

    return {
      ...(styles[hazard] || styles.UNKNOWN),
      weight: 3,
      opacity: 0.95,
      fillOpacity: 0.24,
    };
  }

  function sigmetValue(value, fallback = "Not reported") {
    if (value === null || value === undefined || value === "") {
      return fallback;
    }

    return value;
  }

  function formatSigmetTime(value) {
    if (!value) {
      return "Time unavailable";
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return value;
    }

    return date.toLocaleString([], {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      timeZoneName: "short",
    });
  }

  function sigmetAltitude(properties) {
    const low = properties.altitude_low_1
      ?? properties.altitude_low_2;

    const high = properties.altitude_high_1
      ?? properties.altitude_high_2;

    if (low === null && high === null) {
      return "Not reported";
    }

    const lowText = low !== null && low !== undefined
      ? low.toLocaleString() + " ft"
      : "Surface";

    const highText = high !== null && high !== undefined
      ? high.toLocaleString() + " ft"
      : "Unspecified";

    return lowText + " to " + highText;
  }

  function loadSigmets() {
    return fetch('/api/weather/sigmets', {
      headers: {
        Accept: 'application/json',
      },
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error('SIGMET request failed');
        }

        return response.json();
      })
      .then((data) => {
        sigmetLayer.clearLayers();

        console.log(
          'Sky Blue SIGMET features loaded:',
          (data.features || []).length
        );

        return data.features || [];
      })
      .catch((error) => {
        console.error('Unable to load SIGMET data:', error);
        return [];
      });
  }

  /* END SKY BLUE SIGMET FOUNDATION */

  /*
   * SKY BLUE PIREP FOUNDATION
   */

  const pirepWeatherLayer = leaflet.layerGroup();

  function pirepSeverity(report) {
    const turbulence = String(
      report.turbulence_intensity || ""
    ).toUpperCase();

    const icing = String(
      report.icing_intensity || ""
    ).toUpperCase();

    if (
      turbulence.includes("SEV")
      || turbulence.includes("EXTRM")
      || icing.includes("SEV")
      || icing.includes("EXTRM")
    ) {
      return "severe";
    }

    if (
      turbulence.includes("MOD")
      || icing.includes("MOD")
    ) {
      return "moderate";
    }

    if (
      turbulence.includes("LGT")
      || icing.includes("LGT")
      || turbulence.includes("TRACE")
      || icing.includes("TRACE")
    ) {
      return "light";
    }

    return "none";
  }

  function pirepValue(value, fallback = "Not reported") {
    if (value === null || value === undefined || value === "") {
      return fallback;
    }

    return value;
  }

  function formatPirepTime(value) {
    if (!value) {
      return "Time unavailable";
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return value;
    }

    return date.toLocaleString([], {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      timeZoneName: "short",
    });
  }

  function getVisiblePirepBbox() {
    const bounds = map.getBounds();

    const south = Math.max(-90, bounds.getSouth());
    const west = Math.max(-180, bounds.getWest());
    const north = Math.min(90, bounds.getNorth());
    const east = Math.min(180, bounds.getEast());

    if (west >= east) {
      return null;
    }

    return [
      south.toFixed(2),
      west.toFixed(2),
      north.toFixed(2),
      east.toFixed(2),
    ].join(",");
  }

  function renderPirepMarker(report) {
    const latitude = Number(report.lat);
    const longitude = Number(report.lon);

    if (
      !Number.isFinite(latitude)
      || !Number.isFinite(longitude)
    ) {
      return;
    }

    const severity = pirepSeverity(report);

    const colors = {
      severe: "#dc2626",
      moderate: "#f97316",
      light: "#eab308",
      none: "#38bdf8",
    };

    const color = colors[severity] || colors.none;

    const icon = leaflet.divIcon({
      className: "sbra-pirep-icon",
      html: `
        <span style="
          display:block;
          width:0;
          height:0;
          border-left:7px solid transparent;
          border-right:7px solid transparent;
          border-bottom:14px solid ${color};
          filter:drop-shadow(0 1px 2px rgba(0,0,0,.65));
        "></span>
      `,
      iconSize: [14, 14],
      iconAnchor: [7, 7],
    });

    const marker = leaflet.marker(
      [latitude, longitude],
      {
        icon,
        keyboard: false,
      }
    );

    const altitude = report.altitude_ft
      ? `${Number(report.altitude_ft).toLocaleString()} ft`
      : "Not reported";

    marker.bindTooltip(
      `${report.report_type || "PIREP"} · ${altitude}`,
      {
        direction: "top",
        offset: [0, -7],
      }
    );

    marker.bindPopup(`
      <div class="sbra-pirep-popup">
        <strong>${report.report_type || "PIREP"}</strong><br>
        Aircraft: ${pirepValue(report.aircraft)}<br>
        Altitude: ${altitude}<br>
        Turbulence: ${pirepValue(report.turbulence_intensity)}<br>
        Icing: ${pirepValue(report.icing_intensity)}<br>
        Observed: ${formatPirepTime(report.observation_time)}
        ${
          report.raw
            ? `<div class="sbra-pirep-raw">${report.raw}</div>`
            : ""
        }
      </div>
    `);

    marker.addTo(pirepWeatherLayer);
  }

  function loadWeatherPireps() {
    const bbox = getVisiblePirepBbox();

    if (!bbox) {
      pirepWeatherLayer.clearLayers();
      return Promise.resolve([]);
    }

    const url = `/api/weather/pireps?bbox=${encodeURIComponent(bbox)}`;

    return fetch(url, {
      headers: {
        Accept: "application/json",
      },
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error("PIREP request failed");
        }

        return response.json();
      })
      .then((data) => {
        const reports = data.reports || [];

        pirepWeatherLayer.clearLayers();
        reports.forEach(renderPirepMarker);

        console.log(
          "Sky Blue worldwide PIREPs loaded:",
          reports.length
        );

        return reports;
      })
      .catch((error) => {
        console.error("Unable to load PIREP data:", error);
        return [];
      });
  }

  let pirepMoveTimer = null;

  map.on("moveend", () => {
    if (!map.hasLayer(pirepWeatherLayer)) {
      return;
    }

    window.clearTimeout(pirepMoveTimer);

    pirepMoveTimer = window.setTimeout(() => {
      loadWeatherPireps();
    }, 600);
  });

  /* END SKY BLUE PIREP FOUNDATION */

  /*
   * SKY BLUE METAR FOUNDATION
   */

  const metarLayer = leaflet.layerGroup();

  const metarCategoryStyles = {
    VFR: {
      color: "#16883f",
      fillColor: "#22b14c",
    },
    MVFR: {
      color: "#1765b5",
      fillColor: "#2684d9",
    },
    IFR: {
      color: "#c62828",
      fillColor: "#e53935",
    },
    LIFR: {
      color: "#8e24aa",
      fillColor: "#ab47bc",
    },
    UNKNOWN: {
      color: "#616161",
      fillColor: "#8a8a8a",
    },
  };

  function metarValue(value, suffix = "") {
    if (value === null || value === undefined || value === "") {
      return "Not reported";
    }

    return value + suffix;
  }

  function formatMetarTime(value) {
    if (!value) {
      return "Time unavailable";
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return value;
    }

    return date.toLocaleString([], {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      timeZoneName: "short",
    });
  }

  function loadMetars() {
    return fetch('/api/weather/metars', {
      headers: {
        Accept: 'application/json',
      },
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error('METAR request failed');
        }

        return response.json();
      })
      .then((data) => {
        metarLayer.clearLayers();

        console.log(
          'Sky Blue METAR records loaded:',
          (data.metars || []).length
        );

        return data.metars || [];
      })
      .catch((error) => {
        console.error('Unable to load METAR data:', error);
        return [];
      });
  }

  /* END SKY BLUE METAR FOUNDATION */

  /*
   * SKY BLUE AVIATION WEATHER TOOLBAR
   */

  const AviationWeatherControl = leaflet.Control.extend({
    options: {
      position: 'topleft',
    },

    onAdd() {
      const container = leaflet.DomUtil.create(
        'div',
        'sbra-awx-control leaflet-control'
      );

      container.innerHTML = `
        <button type="button"
                class="sbra-awx-header"
                aria-expanded="true">
          <span class="sbra-awx-title">
            <span class="sbra-awx-icon">WX</span>
            Aviation Weather
          </span>
          <span class="sbra-awx-collapse">−</span>
        </button>

        <div class="sbra-awx-body">
          <div class="sbra-awx-tabs">
            <button type="button"
                    class="sbra-awx-tab active"
                    data-panel="observations">
              Observations
            </button>

            <button type="button"
                    class="sbra-awx-tab"
                    data-panel="forecast">
              Forecast
            </button>

            <button type="button"
                    class="sbra-awx-tab"
                    data-panel="advisories">
              Advisories
            </button>
          </div>

          <div class="sbra-awx-panel active"
               data-panel-content="observations">
            Weather Radar
            <div class="sbra-awx-coming">
              METAR Flight Categories coming next
            </div>
          </div>

          <div class="sbra-awx-panel"
               data-panel-content="forecast">
            Winds Aloft
            <div class="sbra-awx-coming">
              Icing and Turbulence coming later
            </div>
          </div>

          <div class="sbra-awx-panel"
               data-panel-content="advisories">
            <label class="sbra-awx-option">
              <input type="checkbox" data-layer="sigmets">
              <span>SIGMETs</span>
            </label>

            <div class="sbra-sigmet-legend">
              <span><i class="sbra-sigmet-key convective"></i>Convective</span>
              <span><i class="sbra-sigmet-key turbulence"></i>Turbulence</span>
              <span><i class="sbra-sigmet-key icing"></i>Icing</span>
              <span><i class="sbra-sigmet-key other"></i>Other</span>
            </div>

            <label class="sbra-awx-option">
              <input type="checkbox" data-layer="gairmets">
              <span>G-AIRMETs</span>
            </label>

            <div class="sbra-gairmet-legend">
              <span><i class="sbra-gairmet-key ifr"></i>IFR</span>
              <span><i class="sbra-gairmet-key mountain"></i>Mountain Obscuration</span>
              <span><i class="sbra-gairmet-key turbulence"></i>Turbulence</span>
              <span><i class="sbra-gairmet-key icing"></i>Icing</span>
            </div>

            <div class="sbra-awx-coming">
              CWAs coming next
            </div>
          </div>
        </div>
      `;

      leaflet.DomEvent.disableClickPropagation(container);
      leaflet.DomEvent.disableScrollPropagation(container);

      const header = container.querySelector('.sbra-awx-header');
      const body = container.querySelector('.sbra-awx-body');
      const collapse = container.querySelector('.sbra-awx-collapse');

      header.addEventListener('click', () => {
        const isCollapsed = body.classList.toggle('collapsed');

        header.setAttribute(
          'aria-expanded',
          isCollapsed ? 'false' : 'true'
        );

        collapse.textContent = isCollapsed ? '+' : '−';
      });

      container.querySelectorAll('.sbra-awx-tab')
        .forEach((button) => {
          button.addEventListener('click', () => {
            const selectedPanel = button.dataset.panel;

            container.querySelectorAll('.sbra-awx-tab')
              .forEach((tab) => {
                tab.classList.remove('active');
              });

            container.querySelectorAll('.sbra-awx-panel')
              .forEach((panel) => {
                panel.classList.remove('active');
              });

            button.classList.add('active');

            container
              .querySelector(
                '[data-panel-content="' + selectedPanel + '"]'
              )
              .classList.add('active');
          });
        });


      const observationsPanel = container.querySelector(
        '[data-panel-content="observations"]'
      );

      observationsPanel.innerHTML = `
        <label class="sbra-awx-option">
          <input type="checkbox" data-layer="radar">
          <span>Weather Radar</span>
        </label>

        <div class="sbra-awx-slider-row">
          <span>Radar opacity</span>
          <input type="range"
                 min="10"
                 max="100"
                 value="58"
                 data-control="radar-opacity">
        </div>

        <label class="sbra-awx-option">
          <input type="checkbox" data-layer="metars">
          <span>METAR Flight Categories</span>
        </label>

        <div class="sbra-metar-legend">
          <span><i class="sbra-metar-dot vfr"></i>VFR</span>
          <span><i class="sbra-metar-dot mvfr"></i>MVFR</span>
          <span><i class="sbra-metar-dot ifr"></i>IFR</span>
          <span><i class="sbra-metar-dot lifr"></i>LIFR</span>
        </div>

        <label class="sbra-awx-option">
          <input type="checkbox" data-layer="pireps">
          <span>Pilot Reports</span>
        </label>

        <div class="sbra-pirep-legend">
          <span><i class="sbra-pirep-key none"></i>No hazard</span>
          <span><i class="sbra-pirep-key light"></i>Light</span>
          <span><i class="sbra-pirep-key moderate"></i>Moderate</span>
          <span><i class="sbra-pirep-key severe"></i>Severe</span>
        </div>
      `;

      const radarToggle = container.querySelector(
        '[data-layer="radar"]'
      );

      const radarOpacity = container.querySelector(
        '[data-control="radar-opacity"]'
      );

      const metarToggle = container.querySelector(
        "[data-layer=\"metars\"]"
      );

      const pirepToggle = container.querySelector(
        "[data-layer=\"pireps\"]"
      );

      pirepToggle.checked = map.hasLayer(pirepWeatherLayer);

      pirepToggle.addEventListener("change", () => {
        if (pirepToggle.checked) {
          pirepWeatherLayer.addTo(map);
          loadWeatherPireps();
        } else {
          map.removeLayer(pirepWeatherLayer);
        }
      });

      metarToggle.checked = map.hasLayer(metarLayer);

      metarToggle.addEventListener("change", () => {
        if (metarToggle.checked) {
          metarLayer.addTo(map);
          loadMetars();
        } else {
          map.removeLayer(metarLayer);
        }
      });

      radarToggle.checked = map.hasLayer(noaaRadarLayer);

      radarToggle.addEventListener('change', () => {
        if (radarToggle.checked) {
          noaaRadarLayer.addTo(map);
        } else {
          map.removeLayer(noaaRadarLayer);
        }
      });

      radarOpacity.addEventListener('input', () => {
        noaaRadarLayer.setOpacity(
          parseInt(radarOpacity.value, 10) / 100
        );
      });


      const forecastPanel = container.querySelector(
        '[data-panel-content="forecast"]'
      );

      forecastPanel.innerHTML = `
        <label class="sbra-awx-option">
          <input type="checkbox" data-layer="winds">
          <span>Winds Aloft</span>
        </label>

        <label class="sbra-awx-field">
          <span>Altitude</span>
          <select data-control="wind-altitude">
            <option value="3000">3,000 ft</option>
            <option value="6000">6,000 ft</option>
            <option value="9000">9,000 ft</option>
            <option value="12000">12,000 ft</option>
            <option value="18000">18,000 ft</option>
            <option value="24000">24,000 ft</option>
            <option value="30000">30,000 ft</option>
            <option value="34000">34,000 ft</option>
            <option value="39000">39,000 ft</option>
          </select>
        </label>

        <div class="sbra-awx-coming">
          Temperature Aloft coming next
        </div>

        <div class="sbra-awx-coming">
          Icing and Turbulence coming later
        </div>
      `;

      const windsToggle = container.querySelector(
        '[data-layer="winds"]'
      );

      const windAltitude = container.querySelector(
        '[data-control="wind-altitude"]'
      );

      windsToggle.checked = map.hasLayer(windsAloftLayer);
      windAltitude.value = String(selectedWindAltitude);

      windsToggle.addEventListener('change', () => {
        if (windsToggle.checked) {
          windsAloftLayer.addTo(map);
          loadWindsAloft(selectedWindAltitude);
        } else {
          map.removeLayer(windsAloftLayer);
        }
      });

      windAltitude.addEventListener('change', () => {
        selectedWindAltitude = parseInt(
          windAltitude.value,
          10
        );

        if (map.hasLayer(windsAloftLayer)) {
          loadWindsAloft(selectedWindAltitude);
        }
      });

      map.on('overlayadd overlayremove', () => {
        windsToggle.checked = map.hasLayer(windsAloftLayer);
      });

      return container;
    },
  });

  map.addControl(new AviationWeatherControl());

  /* END SKY BLUE AVIATION WEATHER TOOLBAR */

  /* END SKY BLUE WEATHER LAYERS */

  function escapeMapText(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  function createFlightTagIcon(properties) {
    const ident = escapeMapText(properties.ident || 'SBR');
    const departure = escapeMapText(properties.dpt || '');
    const arrival = escapeMapText(properties.arr || '');
    const heading = Number(properties.heading || 0);

    const route = departure && arrival
      ? departure + ' › ' + arrival
      : '';

    const html =
      '<div class="sbra-flight-tag">' +
        '<div class="sbra-flight-tag-ident">' + ident + '</div>' +
        '<div class="sbra-flight-tag-route">' + route + '</div>' +
        '<div class="sbra-flight-tag-pointer" ' +
          'style="transform: rotate(' + heading + 'deg)">' +
          '&#9650;' +
        '</div>' +
      '</div>';

    return leaflet.divIcon({
      className: 'sbra-flight-tag-wrapper',
      html: html,
      iconSize: [122, 68],
      iconAnchor: [61, 53],
    });
  }

  const aircraftIcon = leaflet.icon({
    iconUrl: opts.aircraft_icon,
    iconSize: [42, 42],
    iconAnchor: [21, 21],
  });

  const centerCoords = new LatLng(opts.center[0], opts.center[1]);

  /**
   * Hold the markers
   * @type {{}}
   */
  const markers_list = {};
  let pannedToFlight = false;
  let layerFlights = null;
  let layerSelFlight = null;
  let layerSelFlightFeature = null;
  let layerSelFlightLayer = null;

  const liveMapController = {
    pirep: {},
    pireps: [],
    has_data: false,
    controller: {
      focusMarker: null, // assigned below
    },
  };

  rivets.bind($('#map-info-box'), liveMapController);
  rivets.bind($('#live_flights'), liveMapController);

  function drawRoute(feature, layer, route) {
    if (layerSelFlight !== null) {
      map.removeLayer(layerSelFlight);
    }

    layerSelFlight = new L.Geodesic([], {
      weight: 5,
      opacity: 0.9,
      color: opts.flown_route_color,
      wrap: false,
    }).addTo(map);

    layerSelFlight.fromGeoJson(route.line);
    layerSelFlightFeature = feature;
    layerSelFlightLayer = layer;

    // Center on it, but only do it once, in case the map is moved
    if (!pannedToFlight) {
      map.panTo({
        lat: route.position.lat,
        lng: route.position.lon,
      });

      pannedToFlight = true;
    }
  }

  /**
   * When a flight is clicked on, show the path, etc for that flight
   * @param feature
   * @param layer
   */
  function onFlightClick(feature, layer) {
    const pirep_uri = opts.pirep_uri.replace('{id}', feature.properties.pirep_id);
    const geojson_uri = `${opts.pirep_uri.replace('{id}', feature.properties.pirep_id)}/acars/geojson`;

    /*
     * Run these in parallel:
     * 1. Get information about the PIREP and populate the bottom box/container
     * 2. Draw out the flight route
     */
    request(pirep_uri).then((response) => {
      const pirep = response.data.data;
      console.log(pirep);

      liveMapController.pirep = pirep;
    });

    request(geojson_uri).then((response) => {
      const route = response.data.data;
      console.log(route);

      drawRoute(feature, layer, route);
    });
  }

  /**
   * Focus on a specific marker
   * @param e
   * @param model
   */
  function focusMarker(e, model) {
    if (!(model.pirep.id in markers_list)) {
      console.log('marker not found in list');
      return;
    }

    const marker = markers_list[model.pirep.id];
    onFlightClick(marker[0], marker[1]);
  }

  /*
   * Assign functions to the controller
   */
  liveMapController.controller.drawRoute = drawRoute;
  liveMapController.controller.focusMarker = focusMarker;
  liveMapController.controller.onFlightClick = onFlightClick;

  const updateMap = () => {
    request(opts.acars_uri).then((response) => {
      const pireps = response.data.data;
      liveMapController.pireps = pireps;
      liveMapController.has_data = pireps.length > 0;
    });

    request({ url: opts.update_uri }).then((response) => {
      const flightGeoJson = response.data.data;

      if (layerFlights !== null) {
        layerFlights.clearLayers();
      }

      layerFlights = leaflet.geoJSON(flightGeoJson, {
        onEachFeature: (feature, layer) => {
          layer.on({
            // eslint-disable-next-line no-unused-vars
            click: (e) => {
              pannedToFlight = false;
              liveMapController.controller.onFlightClick(feature, layer);
            },
          });

          let popup_html = '';
          if (feature.properties && (feature.properties.popup !== '' && feature.properties.popup !== undefined)) {
            popup_html += feature.properties.popup;
            layer.bindPopup(popup_html);
          }

          // add to the list
          markers_list[feature.properties.pirep_id] = [feature, layer];
        },
        pointToLayer(feature, latlon) {
          return leaflet.marker(latlon, {
            icon: createFlightTagIcon(feature.properties),
          });
        },
      });

      layerFlights.addTo(map);

      // Reload the clicked-flight information
      if (layerSelFlight !== null) {
        liveMapController.controller.onFlightClick(layerSelFlightFeature, layerSelFlightLayer);
      } else {
        // Center on active flights
        // eslint-disable-next-line no-lonely-if
        if (!pannedToFlight) {
          try {
            map.panTo(layerFlights.getBounds().getCenter());
          } catch (e) {
            map.panTo(centerCoords);
          }
        }
      }
    });
  };

  updateMap();
  setInterval(updateMap, opts.refresh_interval * 1000);
};
